import React, { useState, useEffect, useContext } from 'react';
import LeftArrowIcon from '/src/assets/left-arrow.svg';
import RightArrowIcon from '/src/assets/right-arrow.svg';
import Datapoints from './inputs/datapoints.json';
import Granularities from './inputs/granularity.json';
import ProcessSteps from './inputs/steps.json';
import ProcessStepOutputs from './inputs/step_outputs.json';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import { WindowSizeContext } from '/src/App';
import { MoveLeft, MoveRight } from 'lucide-react';

import './style.css';

const OurProcess = ({ propertyData }) => {

    const { deviceType } = useContext(WindowSizeContext);

    const [currentView, setCurrentView] = useState(1);
    const [currentStep, setCurrentStep] = useState(null);
    const [previousStep, setPreviousStep] = useState(null);
    const [nextStep, setNextStep] = useState(null);
    const [currentDatapoints, setCurrentDatapoints] = useState([]);
    const [currentStepOutputs, setCurrentStepOutputs] = useState([]);
    const [currentDatapointsTruncated, setCurrentDatapointsTruncated] = useState([]);
    const [showAllDatapoints, setShowAllDatapoints] = useState(false);
    const [currentDatapointsToDisplay, setCurrentDatapointsToDisplay] = useState([]);
    const [currentDataFilterNumber, setCurrentDataFilterNumber] = useState(12);
    const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const latitude = propertyData?.data?.property_details?.lat;
    const longitude = propertyData?.data?.property_details?.long;

    const intro_sentence = "Modeling the physical and financial impact of climate risk on a property requires thinking programatically in stages, creatively in permutations, and pragmatically in what actually drives costs. We need to account the risk this property faces individually, but also the risk in the area around it. These are the steps we take to model each property:"

    const getGridClass = (count) => {
        if (!count) return 'grid-2-col';
        if (count === 2) return 'grid-2-col';
        if (count === 3) return 'grid-3-col';
        if (count === 4) return 'grid-4-col';
        if (count <= 6) return 'grid-3-col';
        return 'grid-4-col';
    };


    // useEffect to scroll to top whenever the currentView changes
    useEffect(() => {
        // Find the scrollable container (content-wrapper) instead of using window
        const scrollContainer = document.querySelector('.content-wrapper');
        if (scrollContainer) {
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            // Fallback to window if content-wrapper doesn't exist
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [currentView]);

    useEffect(() => {
        // Find current step
        const current = ProcessSteps.find(step => step.id === currentView);
        setCurrentStep(current);

        // Filter datapoints based on current step's datapoints array
        const filteredDatapoints = current
            ? Datapoints.filter(datapoint => current.datapoints.includes(datapoint.id))
            : [];

        setCurrentDatapoints(filteredDatapoints);

        // Set the number of datapoints to display based on the device type (desktop 12, tablet 9, mobile 6)
        let dataFilterNumber = 12;
        if (deviceType === 'tablet') {
            dataFilterNumber = 9;
        } else if (deviceType === 'mobile') {
            dataFilterNumber = 6;
        }
        setCurrentDataFilterNumber(dataFilterNumber);

        // Create truncated version if needed
        const truncated = filteredDatapoints.slice(0, dataFilterNumber);
        setCurrentDatapointsTruncated(truncated);

        // Reset show all flag when step changes
        setShowAllDatapoints(filteredDatapoints.length <= dataFilterNumber);

        // Set initial display data
        setCurrentDatapointsToDisplay(filteredDatapoints.length <= dataFilterNumber ? filteredDatapoints : truncated);

        // Filter outputs based on current step's outputs array
        const filteredOutputs = current
            ? ProcessStepOutputs.filter(output => current.output.includes(output.id))
            : [];
        setCurrentStepOutputs(filteredOutputs);

        // Find previous step if it exists
        const previous = currentView > 1
            ? ProcessSteps.find(step => step.id === currentView - 1)
            : null;
        setPreviousStep(previous);

        // Find next step if it exists
        const next = currentView < ProcessSteps.length
            ? ProcessSteps.find(step => step.id === currentView + 1)
            : null;
        setNextStep(next);



    }, [currentView, deviceType]);

    // Add effect to handle toggling display
    useEffect(() => {
        setCurrentDatapointsToDisplay(showAllDatapoints ? currentDatapoints : currentDatapointsTruncated);
    }, [showAllDatapoints]);

    const getDynamicContent = (stepId) => {
        switch (stepId) {
            case 1:
                const googleMapUrl = `https://maps.googleapis.com/maps/api/staticmap`
                    + `?center=${latitude},${longitude}`
                    + `&zoom=16`
                    + `&size=533x300`
                    + `&markers=color:0x115451|size:mid|${latitude},${longitude}`
                    + `&key=${GOOGLE_MAPS_KEY}`;

                return (
                    <div className="dynamic-content-box">
                        <img
                            src={googleMapUrl}
                            alt="Property location map"
                            className="property-map"
                        />
                    </div>
                );
            case 2:
                return (
                    <div className="dynamic-content-box">
                        {/* Step 2 specific content */}
                    </div>
                );
            // ... cases for other steps
            default:
                return null;
        }
    };

    return (
        <div className="our-process-container">
            {/* <p className="text-align-center" dangerouslySetInnerHTML={{ __html: intro_sentence }} /> */}

            <div className={`process-steps-container ${deviceType === 'mobile' ? 'mobile' : 'desktop'}`}>
                <div className="process-steps-label">
                    <span>Process Steps</span>
                </div>
                <div className="process-steps-circles">
                    {ProcessSteps.map((step, index) => (
                        <React.Fragment key={step.id}>
                            <div className="step-circle-container">
                                <div
                                    className={`step-circle box-shadow-light ${currentView >= step.id ? 'active' : ''} ${currentView === step.id ? 'selected' : ''}`}
                                    onClick={() => currentView !== step.id && setCurrentView(step.id)}
                                >
                                    {step.id}
                                </div>
                            </div>
                            {index < ProcessSteps.length - 1 && (
                                <div className={`connecting-line ${currentView > step.id ? 'active' : ''}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div className="process-content">
                <div className={`process-header ${deviceType}`}>
                    {previousStep && (
                        <div
                            className="nav-item nav-prev"
                            onClick={() => setCurrentView(currentView - 1)}
                        >
                            <MoveLeft />
                            <div className="nav-text">
                                <span className="nav-label">Back</span>
                                {/* <span className="nav-label">Step {currentView - 1}</span>
                                <span className="nav-title">{previousStep.name}</span> */}
                            </div>
                        </div>
                    )}
                    <span className={`process-header-title text-align-center ${deviceType}`}>Step {currentView}: {currentStep?.name}</span>

                    {nextStep && (
                        <div
                            className="nav-item nav-next"
                            onClick={() => setCurrentView(currentView + 1)}
                        >
                            <div className="nav-text">
                                <span className="nav-label">Next</span>
                                {/* <span className="nav-label">Step {currentView + 1}</span>
                                <span className="nav-title">{nextStep.name}</span> */}
                            </div>
                            <MoveRight />
                        </div>
                    )}
                </div>

                <div className={`process-body ${deviceType !== 'desktop' ? 'mobile' : 'desktop'}`}>
                    <div className={`process-column ${deviceType !== 'desktop' ? 'mobile' : 'desktop'}`}>
                        <div className="process-step-category-name box-shadow-light">
                            <span>What We're Doing</span>
                        </div>
                        <div className="process-dynamic-content">
                            <div className="step-outputs-container">
                                {currentStepOutputs.map(output => (
                                    <div key={output.id} className="step-output-item">
                                        <img
                                            src={CreateCloudfrontUrl(output.icon)}
                                            alt={output.name}
                                            className="output-icon"
                                        />
                                        <span className="output-name text-align-center">{output.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="process-divider">
                            <CurlyBraceDivider />
                        </div>
                        <div className="process-description">
                            <p className="text-align-center" dangerouslySetInnerHTML={{ __html: currentStep?.description }} />
                        </div>
                    </div>

                    {deviceType === 'desktop' ? (
                        <>
                            <div className="vertical-divider"></div>
                        </>
                    ) : (
                        <div className="horizontal-divider"></div>
                    )}

                    <div className={`process-column ${deviceType !== 'desktop' ? 'mobile' : 'desktop'}`}>
                        <div className="process-step-category-name box-shadow-light">
                            <span>Data We Leverage</span>
                        </div>
                        {/* <div className={`datapoints-grid ${getGridClass(currentDatapointsToDisplay.length)}`}> */}
                        <div className={`datapoints-grid`}>
                            {currentDatapointsToDisplay.map(datapoint => (
                                <div key={datapoint.id} className="datapoint-item">
                                    <img
                                        src={CreateCloudfrontUrl(datapoint.icon)}
                                        alt={datapoint.name}
                                        className="datapoint-icon"
                                    />
                                    <span className="datapoint-name text-align-center">{datapoint.name}</span>
                                    <div className="datapoint-granularity-grid">
                                        {Granularities.map(granularity => (
                                            <div
                                                key={granularity.id}
                                                className={`granularity-indicator ${datapoint.granularity.includes(granularity.id)
                                                    ? granularity.name.toLowerCase()
                                                    : 'inactive'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {!showAllDatapoints && currentDatapoints.length > 12 && (
                                <div
                                    className="see-more-button box-shadow-light"
                                    onClick={() => setShowAllDatapoints(true)}
                                >
                                    See {currentDatapoints.length - currentDataFilterNumber} More
                                </div>
                            )}
                        </div>

                        <div className="data-granularity-legend-container">
                            <span className="granularity-title">Data Granularity Used</span>
                            <div className="data-granularity-legend">
                                {Granularities.map(granularity => (
                                    <div key={granularity.id} className="granularity-item">
                                        <div className={`data-granularity-legend-bar ${granularity.name.toLowerCase()}`} />
                                        <span className="granularity-name">{granularity.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="process-navigation">
                {previousStep && (
                    <div
                        className="nav-item nav-prev"
                        onClick={() => setCurrentView(currentView - 1)}
                    >
                        <img src={LeftArrowIcon} alt="Previous step" />
                        <div className="nav-text">
                            <span className="nav-label">Step {currentView - 1}</span>
                            <span className="nav-title">{previousStep.name}</span>
                        </div>
                    </div>
                )}

                {nextStep && (
                    <div
                        className="nav-item nav-next"
                        onClick={() => setCurrentView(currentView + 1)}
                    >
                        <div className="nav-text">
                            <span className="nav-label">Step {currentView + 1}</span>
                            <span className="nav-title">{nextStep.name}</span>
                        </div>
                        <img src={RightArrowIcon} alt="Next step" />
                    </div>
                )}
            </div>
        </div>
    )
}

export default OurProcess;